import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "专注一部影片",
    desc: "我们把注意力放在「蜜桃成熟时3D」这一部作品上，力求把与它相关的信息整理得完整、清楚、便于阅读。",
  },
  {
    icon: "ri-book-open-line",
    title: "克制的表达",
    desc: "在资讯与剧情之外，我们更希望提供一个安静阅读的空间，让每一条信息都能被认真读完。",
  },
  {
    icon: "ri-shield-check-line",
    title: "尊重版权",
    desc: "网站内容均为虚构演示样例，主要用于展示信息组织结构，不涉及任何真实影视作品的版权内容。",
  },
  {
    icon: "ri-heart-3-line",
    title: "为影迷而做",
    desc: "从影迷的真实需求出发，把零散的资讯串联成一条可以慢慢阅读的线索。",
  },
];

const services = [
  { icon: "ri-newspaper-line", title: "影片资讯", desc: "影片动态、幕后访谈、影展资讯与口碑评价的集中整理。" },
  { icon: "ri-book-2-line", title: "剧情解读", desc: "以章节化方式梳理完整故事脉络，方便按顺序或按段落阅读。" },
  { icon: "ri-user-star-line", title: "演员资料", desc: "主演与配角的角色介绍、从业背景与代表作品资料。" },
  { icon: "ri-movie-2-line", title: "影视推荐", desc: "题材与风格相近的影视作品推荐，帮助你拓展观看选择。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 蜜桃成熟时3D",
    description:
      "了解蜜桃成熟时3D影视资讯门户的定位与宗旨：专注呈现影片资讯、剧情解读与演员资料，为影迷提供安静、清晰的阅读体验。",
    keywords: "关于我们,蜜桃成熟时3D,影视资讯门户,网站介绍",
    path: "/about",
  });

  return (
    <>
      <section className="bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-widest text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
            ABOUT US
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">关于我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            「蜜桃成熟时3D」影视资讯门户是一个专注于单部影片信息整理的内容站点。我们围绕这部电影，系统性地梳理资讯、剧情与演员资料，希望为关注这部作品的影迷提供一个清晰、舒适、值得慢慢阅读的空间。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div className="h-72 w-full overflow-hidden rounded-lg md:h-[420px]">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20editorial%20photograph%20of%20an%20open%20notebook%20and%20film%20reel%20on%20a%20rustic%20wooden%20desk%20with%20soft%20golden%20window%20light%2C%20warm%20peach%20and%20amber%20tones%2C%20cozy%20atmospheric%20mood%2C%20high%20detail&width=1000&height=1200&seq=peach-about-01&orientation=portrait"
                alt="关于我们的工作台"
                title="蜜桃成熟时3D 影视资讯门户"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <h2 className="mb-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">品牌故事</h2>
              <p className="mb-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                「蜜桃成熟时3D」讲述的是一个关于成长、离别与重逢的故事。正因为故事本身足够细腻，我们在整理相关信息时，也希望保持同样的耐心——不堆砌信息，而是把每一条内容放在它该在的位置上。
              </p>
              <p className="mb-6 text-sm leading-relaxed text-foreground-600 md:text-base">
                网站从影片资讯出发，延伸出剧情解读与演员资料两条主线，并补充了同类影视作品的推荐。无论你是想快速了解影片动态，还是想从头读一遍完整的故事，都能在这里找到合适的入口。
              </p>
              <div className="rounded-lg border border-primary-100 bg-primary-50 p-5">
                <p className="text-sm leading-relaxed text-foreground-700">
                  <strong className="font-medium text-foreground-950">我们的宗旨：</strong>
                  让每一位影迷，都能以自己舒服的节奏，了解一部电影。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <h2 className="mb-8 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们坚持的几件事</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mb-2 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <h2 className="mb-8 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站提供的内容</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((item) => (
              <div key={item.title} className="rounded-lg bg-background-100 p-6">
                <i className={`${item.icon} mb-4 flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-lg text-background-50`}></i>
                <h3 className="mb-2 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary-50 py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="rounded-lg bg-background-50 p-6 md:p-8">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-copyright-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
                版权声明
              </h2>
              <p className="text-sm leading-relaxed text-foreground-600">
                本网站内的全部文字、图片与数据均为虚构演示样例，仅用于展示页面结构与信息组织方式，不指向任何真实影视作品、人物或机构。页面所用图片为 AI 生成的演示素材，如涉及相似之处纯属巧合。
              </p>
            </div>
            <div className="rounded-lg bg-background-50 p-6 md:p-8">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-lock-2-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
                安全与隐私承诺
              </h2>
              <p className="mb-4 text-sm leading-relaxed text-foreground-600">
                网站不会主动收集你的个人身份信息。收藏等偏好设置仅保存在你的浏览器本地，不会上传至服务器。通过留言表单提交的内容，仅用于与你取得联系。
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700"
              >
                前往联系我们
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}